import compact from 'lodash/compact'
import includes from 'lodash/includes'
import kebabCase from 'lodash/kebabCase'
import map from 'lodash/map'
import trimEnd from 'lodash/trimEnd'
import dynamic from 'next/dynamic'
import React, { ReactNode, useMemo } from 'react'

import SplitLine from '@Components/CMS/SplitLine'
import Trustpilot from '@Components/CMS/Trustpilot'
import {
  AccordionListBlock as AccordionListBlockData,
  ActiveCampaignBlock as ActiveCampaignBlockData,
  Block,
  BlockTypeWithGlobal,
  GalleryBlock as GalleryBlockData,
  GlobalBlock,
  HtmlBlock as HTMLBlockData,
  InfoPanelBlock as InfoPanelBlockData,
  LookDisplayBlock as LookDisplayBlockData,
  LookListBlock as LookListBlockData,
  PageGridBlock as PageGridBlockData,
  PagePresentation,
  PageSliderBlock as PageSliderBlockGraphData,
  ProductListBlock as ProductListBlockData,
  ProductSliderBlock as ProductSliderBlockData,
  SplitTestGroup,
  UspBlock,
} from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import { Experiment } from '@Globals/SplitTests/Experiment'
import { Test } from '@Globals/SplitTests/types'

interface BlockOptions {
  id: string
  idPrefix: string
  ids: Record<string, number>
  index: number
}

type BlockToComponentMap = {
  [key: string]: (block: BlockTypeWithGlobal, options: BlockOptions) => ReactNode
}

const getBlockID = (block: BlockTypeWithGlobal) => `${kebabCase(block.type || '').replace('-block', '')}-${block.id}`

const AccordionListBlock = dynamic(() => import('@Components/CMS/Blocks/AccordionListBlock'))
const ActiveCampaignBlock = dynamic(() => import('@Blocks/ActiveCampaignBlock'))
const CategoryListBlock = dynamic(() => import('@Blocks/CategoryListBlock'))
const ClubSignupBlock = dynamic(() => import('@Blocks/ClubSignup'))
const EmbedCodeBlock = dynamic(() => import('@Components/CMS/Blocks/EmbedCodeBlock'))
const FactBlock = dynamic(() => import('@Components/CMS/Blocks/FactBlock'))
const GalleryBlock = dynamic(() => import('@Components/CMS/Blocks/GalleryBlock'))
const GiftCardBlock = dynamic(() => import('@Components/CMS/Blocks/GiftCardBlock'))
const HTMLBlock = dynamic(() => import('@Blocks/HTML'))
const InfoPanelBlock = dynamic(() => import('@Components/CMS/Blocks/InfoPanelBlock/InfoPanelBlock'))
const ImageBlock = dynamic(() => import('@Blocks/Image'))
const ImageGridBlock = dynamic(() => import('@Components/CMS/Blocks/ImageGridBlock'))
const LeadMagnetBlock = dynamic(() => import('@Components/CMS/Blocks/LeadMagnetBlock'))
const LinkBlock = dynamic(() => import('@Components/CMS/Blocks/LinkBlock'))
const LookDisplayBlock = dynamic(() => import('@Blocks/LookDisplay'))
const LookListBlock = dynamic(() => import('@Blocks/LookList'))
const TagsBlock = dynamic(() => import('@Components/CMS/Blocks/TagsBlock'))
const MediaGalleryBlock = dynamic(() => import('@Components/CMS/Blocks/MediaGalleryBlock'))
const NotifyMeBlock = dynamic(() => import('@Components/CMS/Blocks/NotifyMeBlock'))
const PageGridBlock = dynamic(() => import('@Components/CMS/Blocks/PageGridBlock'))
const PageSliderBlock = dynamic(() => import('@Blocks/PageSliderBlock/PageSliderBlock'))
const ProductAndImageGridBlock = dynamic(() => import('@Components/CMS/Blocks/ProductAndImageGridBlock'))
const ProductDisplayBlock = dynamic(() => import('@Components/CMS/Blocks/ProductDisplayBlock'))
const ProductListBlock = dynamic(() => import('@Blocks/ProductList'))
const ProductSliderBlock = dynamic(() => import('@Blocks/ProductSlider'))
const QuoteBlock = dynamic(() => import('@Components/CMS/Blocks/QuoteBlock'))
const SearchResultsBlock = dynamic(() => import('@Components/CMS/Blocks/SearchResultsBlock'))
const ShippingBlock = dynamic(() => import('@Components/CMS/Blocks/ShippingBlock'))
const ShippingMatrixBlock = dynamic(() => import('@Blocks/ShippingMatrix'))
const SubscribeBlock = dynamic(() => import('@Components/CMS/Blocks/SubscribeBlock'))
const TextBlock = dynamic(() => import('@Blocks/Text'))
const TilesBlock = dynamic(() => import('@Blocks/Tiles'))
const TitleBlock = dynamic(() => import('@Blocks/Title'))
const TrackingDisplayBlock = dynamic(() => import('@Components/CMS/Blocks/TrackingDisplayBlock'))
const UrgencyTimerBlock = dynamic(() => import('@Components/CMS/Blocks/UrgencyTimerBlock'))
const USPBlock = dynamic(() => import('@Blocks/USP'))
const VideoBlock = dynamic(() => import('@Blocks/Video'))
const WishListBlock = dynamic(() => import('@Components/CMS/Blocks/WishListBlock'))

const blockToComponentMap: BlockToComponentMap = {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define,max-len
  accordionListBlock: (block: AccordionListBlockData, { id }) => <AccordionListBlock key={id} accordionListBlock={block} id={id} />,
  activeCampaignBlock: (block: ActiveCampaignBlockData, { id }) => <ActiveCampaignBlock key={id} activeCampaignBlock={block} id={id} />,
  buttonLinkBlock: (block: Block, { id }) => <LinkBlock key={id} id={id} linkBlock={block} />,
  categoryListBlock: (block: Block, { id }) => <CategoryListBlock key={id} categoryListBlock={block} id={id} />,
  clubSignupBlock: (block: Block, { id }) => <ClubSignupBlock key={id} clubSignupBlock={block} id={id} />,
  embedCodeBlock: (block: Block, { id }) => <EmbedCodeBlock key={id} embedCodeBlock={block} id={id} />,
  factBlock: (block: Block, { id }) => <FactBlock key={id} factBlock={block} id={id} />,
  galleryBlock: (block: GalleryBlockData, { id }) => <GalleryBlock key={id} galleryBlock={block} id={id} />,
  giftcardDisplayBlock: (block: Block, { id }) => <GiftCardBlock key={id} giftCardBlock={block} />,
  // TODO: Do we need to support linking to the top of a global block?
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  globalBlock: (block: GlobalBlock, { idPrefix, ids }) => getComponentsForBlocks(block.blocks || [], idPrefix, ids),
  htmlBlock: (block: HTMLBlockData, { id }) => <HTMLBlock key={id} data={block} id={id} />,
  infoPanelBlock: (block: InfoPanelBlockData, { id }) => <InfoPanelBlock block={block} id={id} />,
  imageBlock: (block: Block, { id, index }) => <ImageBlock key={id} data={block} id={id} index={index} />,
  imageGridBlock: (block: Block, { id }) => <ImageGridBlock key={id} id={id} imageGridBlock={block} />,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define,max-len
  injection: (block: GlobalBlock, { id, idPrefix, ids }) => getComponentsForBlocks(block.blocks || [], trimEnd(`${id}-${idPrefix}`, '-'), ids, false, undefined, true),
  // TODO: Do we need to support linking to a lead magnet block, and if so where would it scroll to?
  leadMagnetBlock: (block: Block, { id }) => <LeadMagnetBlock key={id} leadMagnetBlock={block} />,
  linkBlock: (block: Block, { id }) => <LinkBlock key={id} id={id} linkBlock={block} />,
  lookDisplayBlock: (block: LookDisplayBlockData, { id }) => <LookDisplayBlock key={id} data={block} id={id} />,
  lookListBlock: (block: LookListBlockData, { id }) => <LookListBlock key={id} data={block} id={id} />,
  mediaGalleryBlock: (block: Block, { id }) => <MediaGalleryBlock key={id} id={id} mediaGalleryBlock={block} />,
  notifyMeBlock: (block: Block, { id }) => <NotifyMeBlock key={id} id={id} notifyMeBlock={block} />,
  pageGridBlock: (block: PageGridBlockData, { id }) => <PageGridBlock key={id} id={id} pageGridBlock={block} />,
  pageSliderBlock: (block: PageSliderBlockGraphData, { id }) => <PageSliderBlock key={id} id={id} pageSliderBlock={block} />,
  productAndImageGridBlock: (block: Block, { id }) => <ProductAndImageGridBlock key={id} id={id} productAndImageGridBlock={block} />,
  productDisplayBlock: (_, { id }) => <ProductDisplayBlock key={id} id={id} />,
  productListBlock: (block: ProductListBlockData, { id }) => <ProductListBlock key={id} id={id} productListBlock={block} />,
  productSliderBlock: (block: ProductSliderBlockData, { id }) => <ProductSliderBlock key={id} data={block} id={id} />,
  quoteBlock: (block: Block, { id }) => <QuoteBlock key={id} id={id} quoteBlock={block} />,
  searchResultsBlock: (block: Block, { id }) => <SearchResultsBlock key={id} id={id} />,
  splitLineBlock: (block: Block, { id }) => <SplitLine key={id} id={id} />,
  tagsBlock: (block: Block, { id }) => <TagsBlock key={id} id={id} tagBlock={block} />,
  textBlock: (block: Block, { id }) => <TextBlock key={id} data={block} id={id} />,
  tilesBlock: (block: Block, { id }) => <TilesBlock key={id} data={block} id={id} />,
  titleBlock: (block: Block, { id }) => <TitleBlock key={id} data={block} id={id} />,
  trackingDisplayBlock: (block: Block, { id }) => <TrackingDisplayBlock key={id} id={id} />,
  trustpilotBlock: (block: Block, { id }) => <Trustpilot key={id} id={id} />,
  urgencyTimerBlock: (block: Block, { id }) => <UrgencyTimerBlock key={id} id={id} urgencyTimerBlock={block} />,
  uspBlock: (block: Block & UspBlock, { id }) => <USPBlock key={id} data={block} id={id} />,
  videoBlock: (block: Block, { id, index }) => <VideoBlock key={id} blockIndex={index} data={block} id={id} />,
  shippingUrgencyTimerBlock: (block: Block, { id }) => <ShippingBlock key={id} id={id} shippingBlock={block} />,
  shippingMatrixBlock: (block: Block, { id }) => <ShippingMatrixBlock key={id} data={block} id={id} />,
  subscribeBlock: (block: Block, { id }) => <SubscribeBlock key={id} id={id} subscribeBlock={block} />,
  wishListBlock: (block: Block, { id }) => <WishListBlock key={id} id={id} />,
}

const getComponentsForBlocks = (
  blocks: BlockTypeWithGlobal[],
  idPrefix: string,
  ids: Record<string, number> = {},
  shouldFillContainer = false,
  presentation: PagePresentation = PagePresentation.Full,
  isInjection = false,
): ReactNode[] => compact(
  map(
    blocks,
    (block, index) => {
      let id = getBlockID(block)
      // eslint-disable-next-line no-param-reassign
      ids[id] = (ids[id] || 0) + 1

      if (ids[id] > 1) {
        id = `${id}-${ids[id]}`
      }

      if (idPrefix) {
        id = `${idPrefix}-${id}`
      }

      let blockComponent = blockToComponentMap[block.type || '']?.(block, {
        id,
        idPrefix,
        ids,
        index,
      })
      const { splitTest } = block

      if (splitTest?.test) {
        /* eslint-disable react/jsx-no-useless-fragment */
        blockComponent = (
          <Experiment
            a={includes(splitTest.groups, SplitTestGroup.A) ? <>{blockComponent}</> : undefined}
            b={includes(splitTest.groups, SplitTestGroup.B) ? <>{blockComponent}</> : undefined}
            c={includes(splitTest.groups, SplitTestGroup.C) ? <>{blockComponent}</> : undefined}
            d={includes(splitTest.groups, SplitTestGroup.D) ? <>{blockComponent}</> : undefined}
            test={splitTest.test as Test}
          />
        )
        /* eslint-enable react/jsx-no-useless-fragment */
      }

      return (
        <BlockContext.Provider
          key={id}
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{
            blockID: block.id || '',
            blockType: block.type || '',
            id,
            isInjection,
            presentation,
            shouldFillContainer,
          }}
        >
          {blockComponent}
        </BlockContext.Provider>
      )
    },
  ),
)

export const useBlockComponents = (
  blocks: BlockTypeWithGlobal[],
  idPrefix: string = '',
  shouldFillContainer = false,
  presentation: PagePresentation = PagePresentation.Full,
  isInjection = false,
): ReactNode[] => useMemo(
  () => getComponentsForBlocks(
    blocks,
    idPrefix,
    {},
    shouldFillContainer,
    presentation,
    isInjection,
  ),
  [
    blocks,
    idPrefix,
    shouldFillContainer,
    presentation,
    isInjection,
  ],
)
